<template>
    <transition name="trs-side-panel">
        <div class="side-panel grid grid__custom-1"
                :class="rootClasses"
                v-if="isOpen">
            <div class="side-panel--header">
                <div class="grid--container">
                    <div class="grid--row side-panel--header-row">
                        <div class="grid--col">
                            <dropdown :facets="['side-panel']" v-bind="languages"/>
                        </div>
                    </div>
                </div>
            </div>
            <div class="side-panel--breadcrumbs" v-if="currentItem.back_label">
                <transition-group
                        class="grid--container"
                        tag="div"
                        :name="drillAnimationName">
                    <ol class="side-panel--breadcrumbs-row grid--row u-reset" :key="currentItem.menu_item_id">
                        <li class="side-panel--col grid--col">
                            <div class="side-panel--back">
                                <simple-btn :facets="['has-grid']" @click="navigateTo(currentItem.parent_id, 'up')">
                                    <ui-icon symbol="arrow-left" size="medium"/>
                                    {{ currentItem.back_label }}
                                </simple-btn>
                            </div>
                        </li>
                    </ol>
                </transition-group>
            </div>
            <transition-group class="side-panel--menu-wrap" tag="div" :name="drillAnimationName">
                <div class="side-panel--menu" :key="currentItem.menu_item_id">
                    <div class="grid--container">
                        <div class="grid--row">
                            <div class="side-panel--col grid--col">
                                <h2 class="side-panel--category-title u-reset" v-if="currentItem.label">
                                    {{ currentItem.label }}
                                </h2>
                            </div>
                        </div>
                    </div>
                    <nav class="side-panel--group" v-for="(group, index) in currentGroups" :key="index">
                        <div class="grid--container">
                            <div class="grid--row">
                                <div class="side-panel--col grid--col">
                                    <h3 class="side-panel--group-title u-reset" v-if="group.title">
                                        {{ group.title }}
                                    </h3>
                                    <ul class="side-panel--collection u-reset">
                                        <li class="side-panel--item"
                                                v-for="(item, linkIndex) in group.items"
                                                :key="linkIndex">
                                            <a class="side-panel--link u-reset"
                                                    :class="linkFacets(item.facets)"
                                                    :href="item.path"
                                                    :target="item.external ? '_blank' : '_self'"
                                                    v-if="item.path">
                                                {{ item.label }}
                                            </a>
                                            <div class="side-panel--link"
                                                    :class="linkFacets(item.facets)"
                                                    v-else
                                                    @click="navigateTo(item.menu_item_id, 'down')">
                                                {{ item.label }}
                                                <ui-icon symbol="arrow-right" size="medium"/>
                                            </div>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </nav>
                </div>
            </transition-group>
        </div>
    </transition>
</template>

<script>
    /* eslint-disable vue/prop-name-casing */

    import { mapActions, mapState } from 'vuex';
    import bemMixin from '@dreipol/vue-ui/src/mixins/bem';
    import UiIcon from '@dreipol/vue-ui/src/components/icon/icon';
    import Dropdown from 'framework/global-components/dropdown/dropdown';
    import SimpleBtn from 'framework/components/buttons/simple-btn/simple-btn';
    import { subscribe, unsubscribe } from 'util/misc/overlay-tracker';

    export default {
        components: { SimpleBtn, UiIcon, Dropdown },
        mixins: [bemMixin('side-panel')],
        props: {
            /** @type IDropdown */
            languages: {
                type: Object,
                required: true,
            },
            /** @type Array<IMenuItem> */
            menu_items: {
                type: Array,
                required: true,
            },
            /** @type Array<IMenuGroup> */
            menu_groups: {
                type: Array,
                required: true,
            },
            initial_item_id: {
                type: Number,
                default() {
                    return 0;
                },
            },
        },
        data() {
            return {
                currentItemID: this.initial_item_id,
                // this property will be used to handle the menu items transitions
                drillAnimationDirection: 'down',
            };
        },
        computed: {
            ...mapState('side-panel', ['isOpen']),
            currentItem() {
                return this.getMenuItemByID(this.currentItemID);
            },
            currentGroups() {
                if (this.currentItem.group_ids) {
                    return this.filterGroupsByItemID(this.currentItem.group_ids).map(group => {
                        return {
                            ...group,
                            items: group.menu_item_ids.map(id => this.getMenuItemByID(id)),
                        };
                    });
                }

                return [];
            },
            drillAnimationName() {
                return `trs-drill-${ this.drillAnimationDirection }`;
            },
            rootClasses() {
                return this.currentItem.facets && this.currentItem.facets.map(facet => this.bemAdd(facet));
            },
        },
        methods: {
            navigateTo(currentItemID, direction) {
                this.drillAnimationDirection = direction;
                this.currentItemID = currentItemID;
            },
            linkFacets(facets = []) {
                return facets.map(facet => this.bemAdd(facet, 'link'));
            },
            getMenuItemByID(menuItemID) {
                return this.menu_items.find(item => item.menu_item_id === menuItemID);
            },
            filterGroupsByItemID(groupIDs) {
                return this.menu_groups.filter(group => groupIDs.includes(group.group_id));
            },
            ...mapActions('vue-ui/scroll', ['disableScroll']),
            ...mapActions('side-panel', ['close']),
        },
        watch: {
            isOpen(value) {
                this.disableScroll({ isLocked: value });

                if (value) {
                    subscribe(this, this.close);
                } else {
                    this.currentItemID = this.initial_item_id;
                    unsubscribe(this);
                }
            },
        },
        beforeDestroy() {
            unsubscribe(this);
        },
    };
</script>
